import { Box, Button, Grid, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import { Modal } from '@shared/components/modals/Modal';
import { muiTheme } from '@styles/muiTheme';
import { SaveButton } from '@shared/components/SaveButton';
import { useTabs } from '@shared/hooks/useTabs';
import { ButtonsSection, LeftPanel, PageWrapper, RightPanel } from '@styles/DealSheet/DealSheetStyles';
import { Loader } from '@shared/enhancers/withLoader';
import { CancelButton } from '@styles/StyledButtons';
import { deCamelize, formatCurrency, formatDate } from '@shared/commonFunctions';
import {
  dealSummaryData,
  dealSummarySecData,
  fundingDetailsData,
  otherFeeData,
  payOffQuoteData,
  prodData,
  revenueData,
} from '../constants';
import { IDealSheet, IDealSheetPanelProps } from '../interfaces';
import {
  requestCreateDealSheet,
  requestCreateNegative,
  requestGetNewDealSheet,
  requestNegative,
  requestUpdateFundedDate,
} from '../request';
import { Adjustments } from './Adjustments';
import { DealSheetDetails } from './DealSheetDetails';
import { DealSheetHeader } from './DealSheetHeader';
import { DealSheetHistory } from './DealSheetHistory';
import { DealSheetProductSummary } from './DealSheetProductSummary';
import { DealSheetTable } from './DealSheetTable';
import type ExcelJS from 'exceljs';


export const DealSheetPanels = ({ customerName, dealSheets, refresh, dealId }: IDealSheetPanelProps) => {
  const history = useNavigate();
  const { search } = useLocation();
  const { updateCurrentTab } = useTabs();

  const [currentDealSheet, setCurrentDealSheet] = useState<IDealSheet>();
  const [historyIndex, setSelectedHistoryIndex] = useState(0);
  const [isSaveDealSheetModalOpen, setIsSaveDealSheetModalOpen] = useState(false);
  const [isCreateBtnClicked, setCreateBtnClicked] = useState(false);
  const [fundedDate, setFundedDate] = useState<string | null>(currentDealSheet?.fundedDate ?? null);
  const [loading, setLoading] = useState(false);

  const dealSheetSummaryData = dealSummaryData(currentDealSheet);
  const dealSheetSummarySecData = dealSummarySecData(currentDealSheet);
  const fundingDetailsArr = fundingDetailsData(currentDealSheet);
  const otherFeeArr = otherFeeData(currentDealSheet);
  const payOffQuoteArr = payOffQuoteData(currentDealSheet);

  const getNewDealSheetData = useCallback(async () => {
    setLoading(true);
    const resp = await requestGetNewDealSheet(dealId);
    resp && setCurrentDealSheet(resp);
    setLoading(false);
  }, [dealId]);

  const refreshDealSheet = useCallback(() => {
    if (dealSheets) {
      if (dealSheets?.length) {
        refresh();
      } else {
        getNewDealSheetData();
      }
    }
  }, [dealSheets]);

  useEffect(() => {
    if (dealSheets) {
      if (dealSheets?.length) {
        setCurrentDealSheet(dealSheets?.[historyIndex]);
      } else {
        getNewDealSheetData();
      }
    }
  }, [dealSheets, historyIndex]);

  useEffect(() => {
    if (currentDealSheet) {
      setFundedDate(currentDealSheet.fundedDate);
    }
  }, [currentDealSheet]);

  const handleSubmit = useCallback(async () => {
    const updatedData = {
      ...currentDealSheet,
      fundedDate: fundedDate,
    };
    setLoading(true);
    setIsSaveDealSheetModalOpen(false);
    if (isCreateBtnClicked) {
      if (!updatedData?.isNegative) {
        await requestCreateDealSheet(updatedData).then(() => setLoading(false));
      } else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await requestCreateNegative(updatedData!.id ?? '', updatedData).then(() => setLoading(false));
      }
    } else {
      const sheetId = updatedData?.id || dealSheets?.[0]?.id; // or here might not be needed
      // const dealSheet = currentDealSheet?.id ? currentDealSheet : dealSheets?.[0];
      if (sheetId) {
        await requestUpdateFundedDate(sheetId, fundedDate).then(() => setLoading(false));
      } else {
        await requestCreateDealSheet(updatedData).then(() => setLoading(false));
      }
    }

    await refresh();
    setCreateBtnClicked(false);
    setLoading(false);
  }, [
    currentDealSheet,
    requestCreateDealSheet,
    requestUpdateFundedDate,
    requestCreateNegative,
    isCreateBtnClicked,
    dealSheets,
    fundedDate,
  ]);

  const handleOpenSaveDealSheetConfirmationModal = () => {
    setIsSaveDealSheetModalOpen(true);
  };

  const handleCloseSaveDealSheetConfirmationModal = () => {
    setIsSaveDealSheetModalOpen(false);
  };

  const handleDSHistoryClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (rowIds: any) => {
      if (rowIds?.length > 0) {
        const index = dealSheets?.findIndex((item) => item.id === rowIds[0]) ?? 0;

        if (dealSheets?.[index]) {
          await setCurrentDealSheet(dealSheets?.[index]);
        }

        await setSelectedHistoryIndex(index);
      }
    },
    [dealSheets]
  );

  const handleCreateNew = useCallback(async () => {
    setLoading(true);
    setCreateBtnClicked(true);
    getNewDealSheetData();
    setLoading(false);
  }, []);

  interface DataObject {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  }

  const pdfGenerator = useCallback(async () => {
    const doc = new jsPDF({
      unit: 'pt', // points
      format: [612, 792], // Letter size
    });
    const pageWidth = doc.internal.pageSize.getWidth();

    const addMultiLineText = (
      text: string | undefined | null,
      x: number,
      y: number,
      maxWidth: number,
      lineSpacing = 12
    ): number => {
      const textContent: string = text || '';
      const lines: string[] = doc.splitTextToSize(textContent, maxWidth);
      let currentY: number = y;

      for (let i = 0; i < lines.length; i++) {
        doc.text(lines[i], x, currentY);
        currentY += lineSpacing;
      }

      return currentY;
    };

    // VIN - Top left
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('VIN:', 15, 30);
    doc.setFont('helvetica', 'normal');
    doc.text(currentDealSheet?.vin?.toUpperCase() || '', 15, 45);

    doc.setFont('helvetica', 'bold');

    addMultiLineText(`${customerName} - ${currentDealSheet?.dealNumericId ?? ''}`, 15, 70, 200);

    const boxX = 230;
    const boxY = 15;
    const boxWidth = 370;
    const boxHeight = 80;

    // Draw the border
    doc.setLineWidth(1);
    doc.rect(boxX, boxY, boxWidth, boxHeight);

    // First row headers
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    const row1HeaderY = boxY + 15;
    doc.text('LC', boxX + 8, row1HeaderY);
    doc.text('Lender', boxX + 120, row1HeaderY);
    doc.text('Payoff Source', boxX + 250, row1HeaderY);

    // First row values
    doc.setFont('helvetica', 'normal');
    const row1ValueY = row1HeaderY + 15;

    addMultiLineText(currentDealSheet?.lcName, boxX + 8, row1ValueY, 90);
    addMultiLineText(currentDealSheet?.lenderName, boxX + 120, row1ValueY, 90);
    addMultiLineText(currentDealSheet?.payoffSource, boxX + 250, row1ValueY, 100);

    // Second row headers
    doc.setFont('helvetica', 'bold');
    const row2HeaderY = row1ValueY + 25;
    doc.text('Created', boxX + 8, row2HeaderY);
    doc.text('Funded', boxX + 120, row2HeaderY);
    doc.text('Deal Amount Financed', boxX + 250, row2HeaderY);

    // Second row values
    doc.setFont('helvetica', 'normal');
    const row2ValueY = row2HeaderY + 15;
    doc.text(currentDealSheet?.createdDate ? formatDate(currentDealSheet?.createdDate) : '—', boxX + 8, row2ValueY);
    doc.text(currentDealSheet?.fundedDate ? formatDate(currentDealSheet?.fundedDate) : '—', boxX + 120, row2ValueY);
    doc.text(
      currentDealSheet?.cudlAmountFinanced ? formatCurrency(currentDealSheet?.cudlAmountFinanced) : '',
      boxX + 250,
      row2ValueY
    );

    const drawTable = (
      title: string,
      data: DataObject[],
      displayHeader = true,
      rowHeight = 18,
      boxX = 40, // X position of the table
      boxY: number, // Y position of the table
      boxWidth?: number, // Width of the table
      boxHeight?: number // Height of the table
    ) => {
      if (!data || data.length === 0) return 0;

      const colPadding = 10;
      const fontSize = 8;

      const tableY = boxY;

      // Use boxWidth if provided, otherwise calculate based on page width
      const tableWidth = boxWidth || pageWidth - boxX * 2;

      // Title positioning
      doc.setFontSize(10);
      doc.text(title, boxX, tableY);
      let currentY = tableY + 7;

      doc.setFontSize(fontSize);

      // Extract headers
      const headers = Object.keys(data[0]);

      const colWidth = tableWidth / headers.length;

      if (displayHeader) {
        // Draw header row
        headers.forEach((header, i) => {
          const x = boxX + i * colWidth;
          doc.text(deCamelize(header), x + colPadding, currentY + rowHeight / 2);
          // Header bottom line
          doc.line(x, currentY + rowHeight, x + colWidth, currentY + rowHeight);
          // Header right border
          doc.line(x + colWidth, currentY, x + colWidth, currentY + rowHeight);
        });

        // Left border and top border for header
        doc.line(boxX, currentY, boxX, currentY + rowHeight);
        doc.line(boxX, currentY, boxX + tableWidth, currentY);

        currentY += rowHeight;
      }

      // Draw rows
      data.forEach((row) => {
        if (boxHeight && currentY + rowHeight - tableY > boxHeight) {
          return;
        }

        headers.forEach((header, i) => {
          const text = String(row[header] ?? '');
          const x = boxX + i * colWidth;
          doc.text(text, x + colPadding, currentY + rowHeight / 2);
          // Cell borders
          doc.line(x, currentY, x + colWidth, currentY);
          doc.line(x + colWidth, currentY, x + colWidth, currentY + rowHeight);
        });
        // Left border for row
        doc.line(boxX, currentY, boxX, currentY + rowHeight);
        currentY += rowHeight;
      });

      // Bottom border
      doc.line(boxX, currentY, boxX + tableWidth, currentY);

      return currentY + 20;
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const payOffQuoteArrRows = (payOffQuoteArr ?? []).map(({ id, ...rest }) => rest);

    const dealSheetsRows = (dealSheets ?? []).map((dealsheetHistory) => ({
      createdDate: formatDate(dealsheetHistory?.createdDate),
      isNegative: dealsheetHistory?.isNegative ? 'Negative' : '',
    }));

    const adjustmentsRows = (currentDealSheet?.existingAdjustments ?? []).map((adjustmentRow) => ({
      payoffAdjustment: adjustmentRow?.adjustmentPayOff,
      Adjustment: formatCurrency(adjustmentRow?.amountFinanced),
      totalAdjustment: formatCurrency(adjustmentRow?.totalAdjustment),
    }));

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const dealSheetSummaryDataRows = (dealSheetSummaryData ?? []).map(({ id, ...rest }) => rest);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const dealSheetSummarySecDataRows = (dealSheetSummarySecData ?? []).map(({ id, ...rest }) => rest);

    const extraFeeRowCount = (dealSheetSummarySecDataRows ?? []).filter((row) =>
      Object.values(row).some((v) => typeof v === 'string' && (v.trim() === 'FL Stamp Fee' || v.trim() === 'VSI Fee'))
    ).length;

    const baseProductInfoY = 195;
    const baseFundingDetailY = 345;
    const baseOtherFeesY = 345;
    const baseRevenueDetailY = 515;
    const rowHeight = 8;

    const productInfoY = baseProductInfoY + extraFeeRowCount * rowHeight;
    const fundingDetailY = baseFundingDetailY + extraFeeRowCount * rowHeight;
    const otherFeesY = baseOtherFeesY + extraFeeRowCount * rowHeight;
    const revenueDetailY = baseRevenueDetailY + extraFeeRowCount * rowHeight;

    const formatCurrencyField = (value?: string | number): string => {
      return typeof value === 'string' ? value : formatCurrency(value);
    };

    const productInformationDataRows = (prodData(currentDealSheet) ?? []).map((productInformationDataRow) => ({
      type: productInformationDataRow?.type,
      retail: formatCurrencyField(productInformationDataRow?.retail),
      cost: formatCurrencyField(productInformationDataRow?.cost),
      packFee: formatCurrencyField(productInformationDataRow?.packFee),
      productSalesTax: formatCurrencyField(productInformationDataRow?.productSalesTax),
    }));

    const fundingDetailsArrDataRows = (fundingDetailsArr ?? []).map(({ title, value }) => ({ title, value }));

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const otherFeeArrRows = (otherFeeArr ?? []).map(({ id, ...rest }) => rest);

    const revenueDetailsRows = (revenueData(currentDealSheet) ?? []).map((revenueDetailsRow) => ({
      type: revenueDetailsRow?.type,
      commissionable: formatCurrency(revenueDetailsRow?.commissionable),
      nonCommissionable: formatCurrency(revenueDetailsRow?.nonCommissionable),
      total: formatCurrency(revenueDetailsRow?.total),
    }));

    drawTable('Payoff Quote', payOffQuoteArrRows, false, 16, 10, 110, 150, 300);
    drawTable('Deal Sheet History', dealSheetsRows, true, 16, 10, 180, 150, 300);
    drawTable('Existing Adjustment', adjustmentsRows, true, 15, 5, 515, 228, 300);

    drawTable('Deal Summary', dealSheetSummaryDataRows, false, 16, 180, 110, 200, 300);
    drawTable('', dealSheetSummarySecDataRows, false, 16, 400, 110, 200, 300);

    drawTable('Product Information', productInformationDataRows, true, 16, 180, productInfoY, 430, 300);

    drawTable('Funding Detail', fundingDetailsArrDataRows, false, 16, 180, fundingDetailY, 200, 300);
    drawTable('Other Fees', otherFeeArrRows, false, 16, 400, otherFeesY, 200, 300);

    drawTable('Revenue Detail', revenueDetailsRows, true, 16, 235, revenueDetailY, 375, 300);

    // Save
    const fileName = `DealSheet_${currentDealSheet?.dealNumericId}_${formatDate(new Date().toISOString(), {
      format: 'MMDDYYYY',
    })}`;
    doc.save(fileName);
  }, [
    currentDealSheet,
    customerName,
    payOffQuoteArr,
    dealSheets,
    dealSheetSummaryData,
    dealSheetSummarySecData,
    fundingDetailsArr,
    otherFeeArr,
    formatDate,
    formatCurrency,
    deCamelize,
    prodData,
    revenueData,
  ]);

const excelGenerator = useCallback(async () => {
  const { Workbook } = await import('exceljs');
  const wb = new Workbook();
  const ws = wb.addWorksheet('Deal Sheet');

  let cursor = 1;

  const parseValue = (val: string): number | string => {
    if (!val || val === '—') return val;
    const stripped = val.replace(/[$,\s]/g, '').trim();
    const num = Number(stripped);
    return isNaN(num) ? val : num;
  };


  const styleHeaderCell = (cell: ExcelJS.Cell) => {
    cell.font = { name: 'Arial', bold: true, size: 9 };
  };

  const styleTitleCell = (cell: ExcelJS.Cell) => {
    cell.font = { name: 'Arial', bold: true, size: 10 };
  };

  const styleTableHeaderCell = (cell: ExcelJS.Cell) => {
    cell.font = { name: 'Arial', bold: true, size: 8 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } };
    cell.border = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    };
  };

  const styleDataCell = (cell: ExcelJS.Cell, val?: number | string) => {
    cell.font = { name: 'Arial', size: 8 };
    cell.border = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    };
    if (typeof val === 'number') {
      cell.numFmt = '$#,##0.00';
    }
  };

  const writeHeaderBlock = () => {
    const row1 = ws.getRow(cursor++);
    const vinLabel = row1.getCell(1);
    vinLabel.value = 'VIN:';
    styleHeaderCell(vinLabel);
    row1.getCell(2).value = currentDealSheet?.vin?.toUpperCase() ?? '';
    row1.getCell(4).value = `${customerName} - ${currentDealSheet?.dealNumericId ?? ''}`;
    styleHeaderCell(row1.getCell(4));
    row1.commit();

    const row2 = ws.getRow(cursor++);
    const lcLabel = row2.getCell(1);
    lcLabel.value = 'LC:';
    styleHeaderCell(lcLabel);
    row2.getCell(2).value = currentDealSheet?.lcName ?? '';
    const lenderLabel = row2.getCell(4);
    lenderLabel.value = 'Lender:';
    styleHeaderCell(lenderLabel);
    row2.getCell(5).value = currentDealSheet?.lenderName ?? '';
    const payoffLabel = row2.getCell(7);
    payoffLabel.value = 'Payoff Source:';
    styleHeaderCell(payoffLabel);
    row2.getCell(8).value = currentDealSheet?.payoffSource ?? '';
    row2.commit();

    const row3 = ws.getRow(cursor++);
    const createdLabel = row3.getCell(1);
    createdLabel.value = 'Created:';
    styleHeaderCell(createdLabel);
    row3.getCell(2).value = currentDealSheet?.createdDate ? formatDate(currentDealSheet.createdDate) : '—';
    const fundedLabel = row3.getCell(4);
    fundedLabel.value = 'Funded:';
    styleHeaderCell(fundedLabel);
    row3.getCell(5).value = currentDealSheet?.fundedDate ? formatDate(currentDealSheet.fundedDate) : '—';
    const amtLabel = row3.getCell(7);
    amtLabel.value = 'Deal Amount Financed:';
    styleHeaderCell(amtLabel);
    const amtVal = parseValue(
      currentDealSheet?.cudlAmountFinanced ? formatCurrency(currentDealSheet.cudlAmountFinanced) : ''
    );
    const amtCell = row3.getCell(8);
    amtCell.value = amtVal;
    if (typeof amtVal === 'number') amtCell.numFmt = '$#,##0.00';
    row3.commit();

    cursor++; 
  };

  const writeTable = (
    title: string,
    headers: string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rows: Record<string, any>[],
    startRow: number,
    startCol: number,
    displayHeader = true
  ): number => {
    let rowCursor = startRow;

    if (title?.trim()) {
      const titleRow = ws.getRow(rowCursor++);
      const titleCell = titleRow.getCell(startCol);
      titleCell.value = title;
      styleTitleCell(titleCell);
      titleRow.commit();
    }

    if (displayHeader) {
      const headerRow = ws.getRow(rowCursor++);
      headers.forEach((h, i) => {
        const cell = headerRow.getCell(startCol + i);
        cell.value = deCamelize(h);
        styleTableHeaderCell(cell);
      });
      headerRow.commit();
    }

    rows.forEach((rowData) => {
      const dataRow = ws.getRow(rowCursor++);
      Object.values(rowData).forEach((val, i) => {
        const parsed = parseValue(String(val ?? ''));
        const cell = dataRow.getCell(startCol + i);
        cell.value = parsed;
        styleDataCell(cell, parsed);
      });
      dataRow.commit();
    });

    return rowCursor + 1; 
  };



  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const payOffQuoteArrRows = (payOffQuoteArr ?? []).map(({ id, ...rest }) => rest);

  const dealSheetsRows = (dealSheets ?? []).map((ds) => ({
    createdDate: formatDate(ds?.createdDate),
    isNegative: ds?.isNegative ? 'Negative' : '',
  }));

  const adjustmentsRows = (currentDealSheet?.existingAdjustments ?? []).map((adj) => ({
    payoffAdjustment: adj?.adjustmentPayOff,
    Adjustment: formatCurrency(adj?.amountFinanced),
    totalAdjustment: formatCurrency(adj?.totalAdjustment),
  }));

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dealSheetSummaryDataRows = (dealSheetSummaryData ?? []).map(({ id, ...rest }) => rest);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dealSheetSummarySecDataRows = (dealSheetSummarySecData ?? []).map(({ id, ...rest }) => rest);

  const formatCurrencyField = (value?: string | number): string =>
    typeof value === 'string' ? value : formatCurrency(value);

  const productInformationDataRows = (prodData(currentDealSheet) ?? []).map((row) => ({
    type: row?.type,
    retail: formatCurrencyField(row?.retail),
    cost: formatCurrencyField(row?.cost),
    packFee: formatCurrencyField(row?.packFee),
    productSalesTax: formatCurrencyField(row?.productSalesTax),
  }));

  const fundingDetailsArrDataRows = (fundingDetailsArr ?? []).map(({ title, value }) => ({ title, value }));

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const otherFeeArrRows = (otherFeeArr ?? []).map(({ id, ...rest }) => rest);

  const revenueDetailsRows = (revenueData(currentDealSheet) ?? []).map((row) => ({
    type: row?.type,
    commissionable: formatCurrency(row?.commissionable),
    nonCommissionable: formatCurrency(row?.nonCommissionable),
    total: formatCurrency(row?.total),
  }));


  writeHeaderBlock();

  const block1HistoryEnd = writeTable(
    'DealSheet History',
    ['createdDate', 'isNegative'],
    dealSheetsRows,
    cursor,
    1,
    true
  );

  const block1DealSumLeftEnd = writeTable(
    'Deal Summary',
    Object.keys(dealSheetSummaryDataRows[0] ?? {}),
    dealSheetSummaryDataRows,
    cursor,
    4,
    false
  );

  const block1DealSumRightEnd = writeTable(
    '-',
    Object.keys(dealSheetSummarySecDataRows[0] ?? {}),
    dealSheetSummarySecDataRows,
    cursor,
    7,
    false
  );

  const block1ProductEnd = writeTable(
    'Product Information',
    ['type', 'retail', 'cost', 'packFee', 'productSalesTax'],
    productInformationDataRows,
    cursor,
    10,
    true
  );

  cursor = Math.max(block1HistoryEnd, block1DealSumLeftEnd, block1DealSumRightEnd, block1ProductEnd);



  cursor = writeTable(
    'Existing Adjustments',
    ['payoffAdjustment', 'Adjustment', 'totalAdjustment'],
    adjustmentsRows,
    cursor,
    1,
    true
  );



  const block3PayoffEnd = writeTable(
    'Payoff Quote',
    Object.keys(payOffQuoteArrRows[0] ?? {}),
    payOffQuoteArrRows,
    cursor,
    1,
    false
  );

  const block3FundingEnd = writeTable(
    'Funding Detail',
    ['title', 'value'],
    fundingDetailsArrDataRows,
    cursor,
    4,
    false
  );

  const block3OtherFeesEnd = writeTable(
    'Other Fees',
    Object.keys(otherFeeArrRows[0] ?? {}),
    otherFeeArrRows,
    cursor,
    7,
    false
  );

  const block3RevenueEnd = writeTable(
    'Revenue Detail',
    ['type', 'commissionable', 'nonCommissionable', 'total'],
    revenueDetailsRows,
    cursor,
    10,
    true
  );

  cursor = Math.max(block3PayoffEnd, block3FundingEnd, block3OtherFeesEnd, block3RevenueEnd);



  const minColWidths: Record<number, number> = {
    1: 22, 2: 18, 3: 4,        
    4: 22, 5: 18, 6: 4,         
    7: 22, 8: 18, 9: 4,          
    10: 16, 11: 16, 12: 16, 13: 16, 14: 16, 
  };

  ws.columns.forEach((col, i) => {
    const colIndex = i + 1;
    let maxLength = minColWidths[colIndex] ?? 16;

    col.eachCell?.({ includeEmpty: false }, (cell) => {
      const cellValue = cell.value;
      if (cellValue !== null && cellValue !== undefined) {
        const valueLength =
          typeof cellValue === 'number'
            ? formatCurrency(cellValue).length
            : String(cellValue).length;
        if (valueLength > maxLength) {
          maxLength = valueLength;
        }
      }
    });

    col.width = maxLength + 4;
  });


  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `DealSheet_${currentDealSheet?.dealNumericId}_${formatDate(new Date().toISOString(), {
    format: 'MMDDYYYY',
  })}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}, [
  currentDealSheet,
  customerName,
  payOffQuoteArr,
  dealSheets,
  dealSheetSummaryData,
  dealSheetSummarySecData,
  fundingDetailsArr,
  otherFeeArr,
  formatDate,
  formatCurrency,
  deCamelize,
  prodData,
  revenueData,
]);
  
  const requestNavigateBtn = useCallback(async () => {
    if (currentDealSheet?.id || dealSheets?.[historyIndex]?.id) {
      setLoading(true);
      const response = await requestNegative(currentDealSheet?.id ?? dealSheets?.[historyIndex].id ?? '');
      setCurrentDealSheet(response?.data);
      setCreateBtnClicked(true);
      setLoading(false);
    }
  }, [currentDealSheet, requestNegative, dealSheets, historyIndex]);

  const onReturnToDeal = useCallback(() => {
    updateCurrentTab({
      route: `/contact-manager${search}`,
      title: customerName,
    });

    history(`/contact-manager${search}`);
  }, [customerName, search]);

  return (
    <PageWrapper className="dealsheet--panel">
      {currentDealSheet ? (
        <>
          <Formik
            enableReinitialize
            initialValues={currentDealSheet as IDealSheet}
            onSubmit={handleOpenSaveDealSheetConfirmationModal}
          >
            {(props) => (
              <Form onSubmit={props.handleSubmit}>
                <Box sx={{ flexGrow: 1 }}>
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      md={6}
                    >
                      <Typography
                        className="customer-details"
                        variant="h2"
                        color={muiTheme.palette.common.black}
                        fontWeight="800"
                      >
                        {`${customerName}- ${currentDealSheet.dealNumericId ?? ''}`}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      md={6}
                    >
                      <ButtonsSection bml="10px">
                        <SaveButton
                          variant="contained"
                          position="initial"
                          width="64px"
                          disabled={loading}
                          isLoading={loading}
                        />

                        <Button
                          variant="outlined"
                          onClick={onReturnToDeal}
                        >
                          Return To Deal
                        </Button>
                      </ButtonsSection>
                    </Grid>
                    <Grid
                      item
                      md={12}
                    >
                      <ButtonsSection bml="10px">
                        <Button
                          variant="contained"
                          sx={{ marginLeft: 'auto', display: 'block', marginRight: '10px' }}
                          onClick={excelGenerator}
                        >
                          Download Excel
                        </Button>

                        <Button
                          variant="contained"
                          sx={{ marginLeft: 'auto', display: 'block' }}
                          onClick={pdfGenerator}
                        >
                          Download PDF
                        </Button>
                      </ButtonsSection>
                    </Grid>
                  </Grid>
                  <DealSheetHeader data={currentDealSheet} />
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      md={3}
                      sm={12}
                    >
                      <Grid container>
                        <Grid
                          item
                          sm={12}
                        >
                          <DealSheetHistory
                            historyIndex={historyIndex}
                            data={dealSheets ?? []}
                            requestNavigateBtn={requestNavigateBtn}
                            handleCreateNew={handleCreateNew}
                            onClick={handleDSHistoryClick}
                          />
                        </Grid>
                        <Grid
                          item
                          sm={12}
                        >
                          <Adjustments
                            data={currentDealSheet}
                            refresh={refreshDealSheet}
                          />
                        </Grid>
                        <Grid
                          item
                          sm={12}
                        >
                          <DealSheetTable
                            data={payOffQuoteArr}
                            title="Payoff Quote"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      md={9}
                    >
                      <Grid container>
                        <LeftPanel
                          item
                          md={6}
                        >
                          <Grid
                            container
                            spacing={1}
                          >
                            <Grid
                              item
                              md={6}
                            >
                              <DealSheetTable
                                data={dealSheetSummaryData}
                                title="Deal Summary"
                              />
                            </Grid>
                            <Grid
                              item
                              md={6}
                            >
                              <DealSheetTable
                                data={dealSheetSummarySecData}
                                title="."
                              />
                            </Grid>
                          </Grid>
                        </LeftPanel>
                        <RightPanel
                          item
                          md={6}
                        >
                          <DealSheetProductSummary data={currentDealSheet} />
                        </RightPanel>
                      </Grid>
                      <Grid container>
                        <LeftPanel
                          item
                          md={6}
                        >
                          <Grid
                            container
                            spacing={1}
                          >
                            <Grid
                              item
                              md={6}
                            >
                              <DealSheetTable
                                data={fundingDetailsArr}
                                title="Funding Detail"
                                setFundedDate={setFundedDate}
                              />
                            </Grid>
                            <Grid
                              item
                              md={6}
                            >
                              <DealSheetTable
                                data={otherFeeArr}
                                title="Other Fees"
                              />
                            </Grid>
                          </Grid>
                        </LeftPanel>
                        <RightPanel
                          item
                          md={6}
                        >
                          <DealSheetDetails data={currentDealSheet} />
                        </RightPanel>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            )}
          </Formik>
        </>
      ) : loading ? (
        <Loader />
      ) : null}
      {isSaveDealSheetModalOpen && (
        <Modal
          hideBackdrop
          open={isSaveDealSheetModalOpen}
          dialogWidth="600px"
          marginTop="45px"
          title="Save Deal Sheet"
          onClose={handleCloseSaveDealSheetConfirmationModal}
        >
          <Formik
            enableReinitialize
            initialValues={currentDealSheet as IDealSheet}
            onSubmit={handleSubmit}
          >
            <Form>
              <Typography
                variant="h4"
                color={muiTheme.palette.common.black}
              >
                {`Please confirm you would like to create a deal sheet for ${customerName}`}
              </Typography>
              <ButtonsSection bml="10px">
                <SaveButton
                  label="Confirm"
                  variant="contained"
                  position="initial"
                  width="64px"
                  disabled={loading}
                  isLoading={loading}
                />

                <CancelButton
                  variant="outlined"
                  onClick={handleCloseSaveDealSheetConfirmationModal}
                >
                  Cancel
                </CancelButton>
              </ButtonsSection>
            </Form>
          </Formik>
        </Modal>
      )}
    </PageWrapper>
  );
};
